import Renderer from 'apollo-e/rendering/Renderer';
import Object3D from 'apollo-e/rendering/Object3D';
import { Shader, VertexShader, FragmentShader } from 'apollo-e/rendering/Shader';
import { TextureSet, EMPTY_TEXTURE_SET } from 'apollo-e/rendering/Texture';

import Vector2 from 'apollo-e/math/Vector2';
import Vector3 from 'apollo-e/math/Vector3';
import SpecularRGB from 'apollo-e/colors/SpecularRGB';
// import Transform3D from 'apollo-e/transformNodes/Transform3D';

import opacities from 'apollo-e/rendering/OPACITIES';

const defaultVertexScript = require('./shaders/vertex/cc.vert');
const defaultFragmentScript = require('./shaders/fragment/cc.frag');

const shadedVertexScript = require('./shaders/vertex/ccShaded.vert');
const shadedFragmentScript = require('./shaders/fragment/ccShaded.frag');

const defaultVertexShader = new VertexShader(defaultVertexScript, ['aPosition', 'aRotation', 'aColor', 'aOffset', 'aNormal'], ['uMMatrix', 'uCameraPosition']);
const defaultFragmentShader = new FragmentShader(defaultFragmentScript, ['uAmbientLight', 'uDirectionalLight', 'uLightDirection'], []);
const defaultShader = new Shader(defaultVertexShader, defaultFragmentShader);

const shadedVertexShader = new VertexShader(shadedVertexScript, ['aPosition', 'aRotation', 'aColor', 'aOffset', 'aNormal'], ['uMMatrix', 'uCameraPosition', 'uShadowMapVPMatrix']);
const shadedFragmentShader = new FragmentShader(shadedFragmentScript, ['uAmbientLight', 'uDirectionalLight', 'uLightDirection'], ['uShadowMap']);
const shadedShader = new Shader(shadedVertexShader, shadedFragmentShader);

const shaders = {
	default: defaultShader,
	shaded: shadedShader
};

class Particle3D {
	width: number;
	height: number;
	depth: number;
	position: Vector3;
	rotation: Vector3;
	color: SpecularRGB;
	scales: Vector2;
	pivots: Vector2;
	textureSet: TextureSet;
	payload: any;

	constructor () {
		this.width = 1;
		this.height = 1;
		this.depth = 1;
		this.position = new Vector3(0, 0, 0);
		this.rotation = new Vector3(0, 0, 0);
		this.scales = new Vector3(1, 1, 1);
		this.pivots = new Vector3(0.5, 0.5, 0.5);
		this.color = new SpecularRGB(0.5, 0.5, 0.5, 0.01);
		this.textureSet = EMPTY_TEXTURE_SET;
		this.payload = null;
	}

	get scale () {
		return (this.scales.x + this.scales.y + this.scales.z) / 3;
	}

	set scale (scale) {
		this.scales.x = scale;
		this.scales.y = scale;
		this.scales.z = scale;
	}

	get pivot () {
		return (this.pivots.x / this.width + this.pivots.y / this.height + this.pivots.z / this.depth) / 3;
	}

	set pivot (pivot) {
		this.pivots.x = pivot * this.width;
		this.pivots.y = pivot * this.height;
		this.pivots.z = pivot * this.depth;
	}
}

const populateBuffer = {
	position: function (particles: Array<Particle3D>, firstParticle: number, floatView: Float32Array, offset: number, stride: number) {
		// offset /= 4;
		for (let p = firstParticle; p < particles.length; p++) {
			const particle = particles[p];
			const position = particle.position;
			const px = position.x;
			const py = position.y;
			const pz = position.z;
			const faceCount = 6;
			for (let f = 0; f < faceCount; f++) {
				floatView[offset + 0] = floatView[offset + stride + 0] = floatView[offset + stride * 2 + 0] = floatView[offset + stride * 3 + 0] = px;
				floatView[offset + 1] = floatView[offset + stride + 1] = floatView[offset + stride * 2 + 1] = floatView[offset + stride * 3 + 1] = py;
				floatView[offset + 2] = floatView[offset + stride + 2] = floatView[offset + stride * 2 + 2] = floatView[offset + stride * 3 + 2] = pz;
				offset += stride * 4;
			}
		}
	},

	rotation: function (particles: Array<Particle3D>, firstParticle: number, floatView: Float32Array, offset: number, stride: number) {
		// offset /= 4;
		for (let p = firstParticle; p < particles.length; p++) {
			const particle = particles[p];
			const rotation = particle.rotation;
			const rx = rotation.x;
			const ry = rotation.y;
			const rz = rotation.z;
			const faceCount = 6;
			for (let f = 0; f < faceCount; f++) {
				floatView[offset + 0] = floatView[offset + stride + 0] = floatView[offset + stride * 2 + 0] = floatView[offset + stride * 3 + 0] = rx;
				floatView[offset + 1] = floatView[offset + stride + 1] = floatView[offset + stride * 2 + 1] = floatView[offset + stride * 3 + 1] = ry;
				floatView[offset + 2] = floatView[offset + stride + 2] = floatView[offset + stride * 2 + 2] = floatView[offset + stride * 3 + 2] = rz;
				offset += stride * 4;
			}
		}
	},

	color: function (particles: Array<Particle3D>, firstParticle: number, uLongView: Uint32Array, offset: number, stride: number) {
		// offset /= 4;
		for (let p = firstParticle; p < particles.length; p++) {
			const particle = particles[p];
			const color = particle.color;
			const colorCompressed = ((255 * color.r) & 0xff) + (((255 * color.g) & 0xff) << 8) + (((255 * color.b) & 0xff) << 16) + (((255 * color.lobe) & 0xff) << 24);
			const faceCount = 6;
			for (let f = 0; f < faceCount; f++) {
				uLongView[offset + 0] = uLongView[offset + stride] = uLongView[offset + stride * 2] = uLongView[offset + stride * 3] = colorCompressed;
				offset += stride * 4;
			}
		}
	},

	vertices: function (particles: Array<Particle3D>, firstParticle: number, floatView: Float32Array, offset: number, stride: number) {
		// offset /= 4;
		for (let p = firstParticle; p < particles.length; p++) {
			const particle = particles[p];

			const scales = particle.scales;
			const scaleX = scales.x;
			const scaleY = scales.y;
			const scaleZ = scales.z;
	
			const pivots = particle.pivots;
			const x0 = -pivots.x * scaleX;
			const y0 = -pivots.y * scaleY;
			const z0 = -pivots.z * scaleZ;
	
			const x1 = x0 + particle.width * scaleX;
			const y1 = y0 + particle.height * scaleY;
			const z1 = z0 + particle.depth * scaleZ;

			// vertex 0
			floatView[offset + 0] = floatView[offset + stride * 13 + 0] = floatView[offset + stride * 22 + 0] = x0;
			floatView[offset + 1] = floatView[offset + stride * 13 + 1] = floatView[offset + stride * 22 + 1] = y0;
			floatView[offset + 2] = floatView[offset + stride * 13 + 2] = floatView[offset + stride * 22 + 2] = z1;

			// vertex 1
			floatView[offset + stride + 0] = floatView[offset + stride * 4 + 0] = floatView[offset + stride * 23 + 0] = x1;
			floatView[offset + stride + 1] = floatView[offset + stride * 4 + 1] = floatView[offset + stride * 23 + 1] = y0;
			floatView[offset + stride + 2] = floatView[offset + stride * 4 + 2] = floatView[offset + stride * 23 + 2] = z1;

			// vertex 2
			floatView[offset + stride * 2 + 0] = floatView[offset + stride * 15 + 0] = floatView[offset + stride * 16 + 0] = x0;
			floatView[offset + stride * 2 + 1] = floatView[offset + stride * 15 + 1] = floatView[offset + stride * 16 + 1] = y1;
			floatView[offset + stride * 2 + 2] = floatView[offset + stride * 15 + 2] = floatView[offset + stride * 16 + 2] = z1;

			// vertex 3
			floatView[offset + stride * 3 + 0] = floatView[offset + stride * 6 + 0] = floatView[offset + stride * 17 + 0] = x1;
			floatView[offset + stride * 3 + 1] = floatView[offset + stride * 6 + 1] = floatView[offset + stride * 17 + 1] = y1;
			floatView[offset + stride * 3 + 2] = floatView[offset + stride * 6 + 2] = floatView[offset + stride * 17 + 2] = z1;

			// vertex 4
			floatView[offset + stride * 8 + 0] = floatView[offset + stride * 14 + 0] = floatView[offset + stride * 18 + 0] = x0;
			floatView[offset + stride * 8 + 1] = floatView[offset + stride * 14 + 1] = floatView[offset + stride * 18 + 1] = y1;
			floatView[offset + stride * 8 + 2] = floatView[offset + stride * 14 + 2] = floatView[offset + stride * 18 + 2] = z0;

			// vertex 5
			floatView[offset + stride * 9 + 0] = floatView[offset + stride * 7 + 0] = floatView[offset + stride * 19 + 0] = x1;
			floatView[offset + stride * 9 + 1] = floatView[offset + stride * 7 + 1] = floatView[offset + stride * 19 + 1] = y1;
			floatView[offset + stride * 9 + 2] = floatView[offset + stride * 7 + 2] = floatView[offset + stride * 19 + 2] = z0;

			// vertex 6
			floatView[offset + stride * 10 + 0] = floatView[offset + stride * 12 + 0] = floatView[offset + stride * 20 + 0] = x0;
			floatView[offset + stride * 10 + 1] = floatView[offset + stride * 12 + 1] = floatView[offset + stride * 20 + 1] = y0;
			floatView[offset + stride * 10 + 2] = floatView[offset + stride * 12 + 2] = floatView[offset + stride * 20 + 2] = z0;

			// vertex 7
			floatView[offset + stride * 11 + 0] = floatView[offset + stride * 5 + 0] = floatView[offset + stride * 21 + 0] = x1;
			floatView[offset + stride * 11 + 1] = floatView[offset + stride * 5 + 1] = floatView[offset + stride * 21 + 1] = y0;
			floatView[offset + stride * 11 + 2] = floatView[offset + stride * 5 + 2] = floatView[offset + stride * 21 + 2] = z0;

			offset += stride * 24;
		}
	},

	normals: function (particles: Array<Particle3D>, firstParticle: number, floatView: Float32Array, offset: number, stride: number) {
		// offset /= 4;
		for (let p = firstParticle; p < particles.length; p++) {
			// const particle = particles[p];
			let nx = 0;
			let ny = 0;
			let nz = 1;
			const faceCount = 6;

			// face A: (0, 0, 1)
			// face B: (1, 0, 0)
			// face C: (0, 0, -1)
			// face D: (-1, 0, 0)
			// face E: (0, -1, 0)
			// face F: (0, 1, 0)
			for (let f = 0; f < faceCount; f++) {
				floatView[offset + 0] = floatView[offset + stride + 0] = floatView[offset + stride * 2 + 0] = floatView[offset + stride * 3 + 0] = nx;
				floatView[offset + 1] = floatView[offset + stride + 1] = floatView[offset + stride * 2 + 1] = floatView[offset + stride * 3 + 1] = ny;
				floatView[offset + 2] = floatView[offset + stride + 2] = floatView[offset + stride * 2 + 2] = floatView[offset + stride * 3 + 2] = nz;
				offset += stride * 4;
				const nx0 = nx;
				nx = nz;
				if (nx0 === -1) {
					ny = -1;
					nz = 0;
				} else {
					ny = -ny;
					nz = -nx0;
				}
			}
		}
	}

};

class DynamicProperties {
	position: boolean;
	rotation: boolean;
	vertices: boolean;
	color: boolean;
	normals: boolean;

	constructor (dynamicProperties: Array<string>) {
		this.position = false;
		this.rotation = false;
		this.vertices = false;
		this.color = false;
		this.normals = false;

		for (let p = 0; p < dynamicProperties.length; p++) {
			this[dynamicProperties[p]] = true;
		}
	}
}

class Attribute {
	name: string;
	count: number;
	type: number;
	normalize: boolean;
	byteSize: number;
	byteStride: number;

	constructor (name: string, count: number, normalize: boolean, byteSize: number) {
		this.name = name;
		this.count = count;
		this.normalize = normalize;
		this.byteSize = byteSize;
		this.byteStride = -1;
		this.type = -1;
	}
}

const defaultMaxBatchSize = 4096;
const verticesPerParticle = 24;

export default class QTContainer3D extends Object3D {
	shaders = shaders;

	particles: Array<Particle3D>;
	private _particleUpdateOffset: number;
	private _buffers: { [property: string]: Float32Array | Uint32Array };
	private _bufferBinders: Array<{ [property: string]: WebGLBuffer }>;
	private _attributes: { [property: string]: Attribute };
	private _dynamicProperties: DynamicProperties;
	private _dynamicBuffer: ArrayBuffer;
	private _staticBuffer: ArrayBuffer;
	private _dynamicBufferView: Float32Array;
	private _staticBufferView: Float32Array;
	private _dynamicStride: number;
	private _staticStride: number;
	private _maxBatchSize: number;

	constructor (dynamicProperties: Array<string>, maxBatchSize: number = defaultMaxBatchSize) {
		super();
		this.opacity = opacities.OPAQUE;
		this.castShadow = false;

		this._particleUpdateOffset = 0;
		this.particles = [];

		this._dynamicProperties = new DynamicProperties(dynamicProperties);

		this._bufferBinders = [];

		this._attributes = {
			position: new Attribute('aPosition', 3, false, 4 * 3),
			rotation: new Attribute('aRotation', 3, false, 4 * 3),
			vertices: new Attribute('aOffset', 3, false, 4 * 3),
			color: new Attribute('aColor', 4, true, 1 * 4),
			normals: new Attribute('aNormal', 3, false, 4 * 3)
		};

		this._dynamicStride = 0;
		this._staticStride = 0;
		for (let property in this._dynamicProperties) {
			const attribute = this._attributes[property];
			if (this._dynamicProperties[property] === true) {
				attribute.byteStride = this._dynamicStride;
				this._dynamicStride += attribute.byteSize;
			} else {
				attribute.byteStride = this._staticStride;
				this._staticStride += attribute.byteSize;
			}
		}

		this._maxBatchSize = maxBatchSize;
		const maxVertexCount = verticesPerParticle * maxBatchSize;
		this._staticBuffer = new ArrayBuffer(maxVertexCount * this._staticStride);
		this._dynamicBuffer = new ArrayBuffer(maxVertexCount * this._dynamicStride);
		this._dynamicBufferView = new Float32Array(this._dynamicBuffer);
		this._staticBufferView = new Float32Array(this._staticBuffer);

		const ArrayBufferTypes = {
			position: Float32Array,
			rotation: Float32Array,
			vertices: Float32Array,
			color: Uint32Array,
			normals: Float32Array,
		};

		this._buffers = {};
		for (let property in this._dynamicProperties) {
			if (this._dynamicProperties[property] === true) {
				this._buffers[property] = new ArrayBufferTypes[property](this._dynamicBuffer);
			} else {
				this._buffers[property] = new ArrayBufferTypes[property](this._staticBuffer);
			}
		}
	}

	createParticle () {
		if (this.particles.length >= this._maxBatchSize) {
			throw new Error('Could not create new particle: max particle count reach');
		}

		const particle = new Particle3D();
		particle.textureSet = this.textureSet;
		this.particles.push(particle);
		return particle;
	}

	removeParticle (particle: Particle3D) {
		const index = this.particles.indexOf(particle);
		if (index !== -1) {
			this.particles.splice(index, 1);
			this._particleUpdateOffset = Math.min(this._particleUpdateOffset, index);
		}
	}

	private _initializeBufferBinders (gl: WebGLRenderingContext) {
		const staticBufferBinder = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, staticBufferBinder);
		gl.bufferData(gl.ARRAY_BUFFER, this._staticBuffer, gl.STATIC_DRAW);

		const dynamicBufferBinder = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, dynamicBufferBinder);
		gl.bufferData(gl.ARRAY_BUFFER, this._dynamicBuffer, gl.DYNAMIC_DRAW);

		if (staticBufferBinder === null || dynamicBufferBinder === null) {
			throw new Error('Failed to initalize buffers');
		}

		this._attributes.position.type = gl.FLOAT;
		this._attributes.rotation.type = gl.FLOAT;
		this._attributes.vertices.type = gl.FLOAT;
		this._attributes.color.type = gl.UNSIGNED_BYTE;
		this._attributes.normals.type = gl.FLOAT;

		return {
			static: staticBufferBinder,
			dynamic: dynamicBufferBinder,
		};
	}

	render (renderer: Renderer, shader: Shader) {
		const gl = renderer.gl;

		if (this.particles.length === 0) {
			return;
		}

		let bufferBinders = this._bufferBinders[renderer.id];
		if (bufferBinders === undefined) {
			this._bufferBinders[renderer.id] = bufferBinders = this._initializeBufferBinders(gl);
		}

		const program = shader.program;
		const attributes = program.attributes;

		// uploading static particles
		gl.bindBuffer(gl.ARRAY_BUFFER, bufferBinders.static);
		for (let property in this._dynamicProperties) {
			if (this._dynamicProperties[property] === false) {
				const attribute = this._attributes[property];
				if (this._particleUpdateOffset < this.particles.length) {
					const buffer = this._buffers[property];
					populateBuffer[property](this.particles, this._particleUpdateOffset, buffer, this._particleUpdateOffset * this._staticStride * verticesPerParticle / 4 + attribute.byteStride / 4, this._staticStride / 4);
					// if (this._particleUpdateOffset !== 0) {
					// 	debugger
					// }
				}
				gl.vertexAttribPointer(attributes[attribute.name], attribute.count, attribute.type, attribute.normalize, this._staticStride, attribute.byteStride);
			}
		}

		if (this._particleUpdateOffset < this.particles.length) {
			const staticLongStride = this._staticStride * verticesPerParticle / 4;
			gl.bufferSubData(gl.ARRAY_BUFFER, this._particleUpdateOffset * staticLongStride * 4, this._staticBufferView.subarray(this._particleUpdateOffset * staticLongStride, this.particles.length * staticLongStride));
			this._particleUpdateOffset = this.particles.length;
		}

		gl.bindBuffer(gl.ARRAY_BUFFER, bufferBinders.dynamic);
		for (let property in this._dynamicProperties) {
			if (this._dynamicProperties[property] === true) {
				const attribute = this._attributes[property];
				const buffer = this._buffers[property];
				populateBuffer[property](this.particles, 0, buffer, 0 * this._dynamicStride + attribute.byteStride / 4, this._dynamicStride / 4);
				gl.vertexAttribPointer(attributes[attribute.name], attribute.count, attribute.type, attribute.normalize, this._dynamicStride, attribute.byteStride);
			}
		}

		if (this._dynamicStride > 0) {
			gl.bufferSubData(gl.ARRAY_BUFFER, 0, this._dynamicBufferView.subarray(0, this.particles.length * this._dynamicStride * verticesPerParticle / 4));
		}

		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, renderer.quadIndexBuffer);
		gl.drawElements(gl.TRIANGLES, 36 * this.particles.length, gl.UNSIGNED_SHORT, 0);
	}

	renderDefault (renderer: Renderer) {
		this.render(renderer, defaultShader);
	}

	renderShaded (renderer: Renderer) {
		this.render(renderer, shadedShader);
	}
}
